import { IComponentOptions, IPromise, IQService, IScope, noop } from "angular";
import { StateService } from "@uirouter/angularjs";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { GtmhubController } from "@gtmhub/core";
import { storage } from "@gtmhub/core/storage";
import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalFormEventType, IBulkCreateGoalRequest, IGoal, IOkrTemplate, IPolicy } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { PolicyService } from "@gtmhub/goals/services/policy.service";
import { getDefaultAssigneeId, getDefaultGoalBase, getGoalNamePolicy } from "@gtmhub/goals/utils";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { OkrEvents } from "@gtmhub/okrs/events";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { PlanningSessionSelector } from "@gtmhub/sessions/services/planning-session-selector";
import { PlanningSessionService } from "@gtmhub/sessions/services/planning-session.service";
import { SingleCollectionSearchItem } from "@gtmhub/shared/components/single-collection-search/single-collection-search.component";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { ITeam, ITeamsStoreState, TeamsActions, addAssigneeDataToTeams } from "@gtmhub/teams";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { PermissionsService } from "@gtmhub/users/permissions-service";
import { Flavor, IRecommendationArgs, recommendation } from "@webapp/analytics/models/analytics.model";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { Assignee, AssigneeSelectorType } from "@webapp/assignees/models/assignee.models";
import { CfMap, ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { filterCustomFieldsBySession } from "@webapp/custom-fields/utils/custom-fields-filter-util";
import { ISessionsIdMap, IUserItemAllowedActions, Session } from "@webapp/sessions/models/sessions.model";
import { OkrMethodologyFacade } from "./facades/okr-methodology.facade";

interface ICreateFirstGoalRedux {
  assigneeIdMap: Record<string, Assignee>;
  sessionIdMap: ISessionsIdMap;
  teams: ITeam[];
  reduxStatesInitialized: boolean;
  reduxStateError: IUIError;
}

interface ICreateFirstGoalFormBindings {
  close(): void;
  dismiss(): void;
  resolve: {
    externalSessionId: string;
    isOnlyOpenSession: boolean;
    shouldStartWithTemplate: boolean;
  };
}

const bindStateToThis = (state: ISessionsStoreState & IAssigneesStoreState & ITeamsStoreState): ICreateFirstGoalRedux => {
  const { map: assignees, isFetched: assigneesFetched, error: assigneesError } = state.assignees;
  const { map: sessions, isFetched: sessionsFetched, error: sessionsError } = state.sessions;
  const { items: teams, isFetched: teamsFetched, error: teamsError } = state.teams;

  return {
    assigneeIdMap: assignees,
    sessionIdMap: sessions,
    teams: addAssigneeDataToTeams(teams, assignees),
    reduxStatesInitialized: assigneesFetched && sessionsFetched && teamsFetched,
    reduxStateError: assigneesError || sessionsError || teamsError,
  };
};

type CreateFirstGoalFormCurrentView = "startWithTemplate" | "startOnYourOwn";

interface ICreateFirstGoalFormScope extends IScope, IExtendedRootScopeService {}

let cachedNewGoal = null;
let cacheTimestamp = Date.now();
const isCacheTimedout = (): boolean => {
  const cacheTimeout = 5 * 60 * 1000; // 5 minutes in ms
  return Date.now() - cacheTimestamp > cacheTimeout;
};
const relevantOkrTemplatesCount = 4;
const recommendedOkrTemplatesCount = 3;

export interface CreateFirstGoalFormCtrl extends ICreateFirstGoalFormBindings, ICreateFirstGoalRedux {}
export class CreateFirstGoalFormCtrl extends GtmhubController {
  currentView: CreateFirstGoalFormCurrentView;
  recommendedOkrTemplates: IOkrTemplate[];
  isAdmin: boolean;
  selectedOwner: Assignee;
  screenAllowed: {
    externalSession: boolean;
    selectedSession: boolean;
  };
  goalsLimitReach: {
    externalSession: boolean;
    selectedSession: boolean;
  };
  showGoalsLimitWarning: boolean;
  selectedOwnerGoalsCount: number;
  selectedSessions: {
    externalSession: Session;
    selectedSession: Session;
  };
  goal: IGoal;
  goalNamePolicy: IPolicy;
  indicators: {
    loading?: IIndicator;
    saving?: IIndicator;
    loadingPolicy?: IIndicator;
    loadingTemplates?: IIndicator;
  };
  requiredCfNotEmpty: boolean;
  assignGoalPermissions: AssigneeSelectorType;
  goalsLimit: number;

  private currentUserId: string;
  private allowedActions: string[];
  private okrEvents: OkrEvents;
  private isCurrentCallExternal: boolean;
  private installOkrsCb: () => void;

  static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "GoalService",
    "PlanningSessionService",
    "PermissionsService",
    "UIErrorHandlingService",
    "$q",
    "PolicyService",
    "AnalyticsService",
    "AssigneeActions",
    "PlanningSessionsActions",
    "$ngRedux",
    "PlanningSessionSelector",
    "TeamsActions",
    "OkrMethodologyFacade",
    "CurrentUserService",
  ];

  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private $scope: ICreateFirstGoalFormScope,
    private $state: StateService,
    private goalService: GoalService,
    private planningSessionService: PlanningSessionService,
    private permissionsService: PermissionsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $q: IQService,
    private policyService: PolicyService,
    private analyticsService: AnalyticsService,
    private assigneeActions: AssigneeActions,
    private planningSessionsActions: PlanningSessionsActions,
    private $ngRedux: INgRedux,
    private planningSessionSelector: PlanningSessionSelector,
    private teamsActions: TeamsActions,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private currentUserService: CurrentUserService
  ) {
    super();

    // creating the goal object so sessionId can be added during permissions
    this.goal = getDefaultGoalBase();

    this.screenAllowed = {
      externalSession: false,
      selectedSession: true,
    };

    this.goalsLimitReach = {
      externalSession: false,
      selectedSession: false,
    };

    this.selectedSessions = {
      externalSession: null,
      selectedSession: null,
    };

    this.indicators = {
      loading: { progress: true },
    };

    const metaGoalFormOpened = { type: "create" };
    this.analyticsService.track(GoalFormEventType.GOAL_FORM_OPENED, metaGoalFormOpened);

    this.okrEvents = new OkrEvents($scope);

    this.currentUserId = getCurrentUserId();
    this.isAdmin = this.currentUserService.userHasRole("admin");

    this.onDestroy(this.$ngRedux.connect(bindStateToThis)(this));
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());
  }

  $onInit(): void {
    if (this.shouldAbortOnMissingExternalSessionId()) {
      return;
    }

    this.switchToView("startOnYourOwn");

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.reduxStatesInitialized,
        () => this.reduxStateError
      )
      .then(
        () => {
          this.getGoalPolicy();
          this.init();
        },
        () => this.uiErrorHandlingService.handleModal(this.reduxStateError)
      )
      .catch((error) => this.uiErrorHandlingService.handleModal(error));
  }

  $onDestroy(): void {
    // We want to save the state of the modal only if it was closed with backdrop
    // We consider this as accidental form close and we want to save the state of the form
    const modalCloseReason = String(storage.get("modalCloseReason"));
    if (modalCloseReason && modalCloseReason.includes("backdrop")) {
      cacheTimestamp = Date.now();
      cachedNewGoal = this.goal;
    } else {
      cachedNewGoal = null;
    }
  }

  switchToView(targetView: CreateFirstGoalFormCurrentView, { shouldSkipWarning } = { shouldSkipWarning: false }): void {
    if (!shouldSkipWarning) {
      this.showGoalsLimitWarning = true;
    }

    this.updateCurrentView(targetView);

    if (targetView === "startWithTemplate" && !this.recommendedOkrTemplates) {
      this.getRecommendedGoals();
    }
  }

  public trackRecommendationHelpfulness(args: IRecommendationArgs): void {
    this.analyticsService.track(recommendation, {
      is_useful: args.isUseful,
      flavor: Flavor.PASSIVE,
      action: "creates_first_goal_trial",
    });
  }

  selectSessionToInstallTemplate(item: IBulkCreateGoalRequest, { isExternalCall } = { isExternalCall: false }): void {
    this.isCurrentCallExternal = isExternalCall;
    if (isExternalCall) {
      this.goal.name = item.name;
    }

    // Use externalSessionId
    if (this.resolve.isOnlyOpenSession) {
      this.installOkrsCb = () => this.installOkrs(item, this.selectedSessions.externalSession.id);
      this.setSelectedSessions({ externalSession: this.selectedSessions.externalSession, selectedSession: {} });
      this.setGoalAssignee();
      return;
    }

    // Choose an open session
    this.planningSessionSelector.selectSession().then((selectedSession) => {
      this.installOkrsCb = () => this.installOkrs(item, selectedSession.id);
      this.setSelectedSessions({ externalSession: this.selectedSessions.externalSession, selectedSession: selectedSession });
      this.setGoalAssignee();
    });
  }

  save(): void {
    this.indicators.saving = { progress: true };

    if (this.selectedSessions.externalSession) {
      this.goal.dateFrom = this.selectedSessions.externalSession.start;
      this.goal.dateTo = this.selectedSessions.externalSession.end;
    }

    // there will always be only one owner in this form
    this.checkGoalLimitAndPermissions(this.goal.ownerIds[0]).then(
      (goalsLimitReach) => {
        if (goalsLimitReach) {
          return;
        }

        this.goalService.createGoal(this.goal, { skipNotification: false }).then((goal) => {
          this.$rootScope.$broadcast("goalsChanged");
          delete this.indicators.saving;

          this.$state.go("gtmhub.home.dashboard._goals._goalSidebar.goal", { id: goal.id });
        });
      },
      (error) => (this.indicators.saving = { error })
    );
  }

  onSearchInputChange(searchTerm: string): IPromise<SingleCollectionSearchItem[]> {
    this.goal.name = searchTerm;

    if (searchTerm) {
      this.indicators.saving = { progress: true };

      return this.goalService.getGoalTemplatesFromMarketplace({ skip: 0, take: relevantOkrTemplatesCount, text: searchTerm }).then(
        (goalTemplatesResponse) => {
          delete this.indicators.saving;

          return this.enrichRelevantOkrTemplates(goalTemplatesResponse.items);
        },
        (error) => {
          this.indicators.saving = { error };
          return [];
        }
      );
    }

    return this.$q.resolve([]);
  }

  adjustModalHeightOnSearch(itemCount: number): void {
    const itemHeight = 30;
    const searchCopyAndActionsHeight = 50;
    const minSearchResultsHeight = 100;
    const spacer = document.querySelector<HTMLElement>(".spacer");
    const editableCfHeight = document.querySelector<HTMLElement>(".editable-custom-fields")?.offsetHeight || 0;
    const searchResultsHeight = itemCount * itemHeight + searchCopyAndActionsHeight;
    const calculatedHeight = searchResultsHeight > minSearchResultsHeight ? searchResultsHeight - editableCfHeight : minSearchResultsHeight - editableCfHeight;
    if (spacer) {
      spacer.style.height = itemCount ? `${calculatedHeight.toString()}px` : "0px";
    }
  }

  browseMoreTemplates(location: "CreateFirstGoalFormCtrl" | "SingleCollectionSearchCtrl"): void {
    this.analyticsService.track("Navigation OKRs clicked", { location });
    window.open("/marketplace/okrs-examples", "_blank");
  }

  updateCustomFields = (customFields: CfMap): void => {
    this.goal.customFields = customFields;

    this.$scope.$evalAsync();
  };

  setRequiredCf = (requiredCf: { areFilled: boolean }): void => {
    this.requiredCfNotEmpty = requiredCf.areFilled;

    this.$scope.$evalAsync();
  };

  public filterBySessionId = (customFields: ICustomField[]): ICustomField[] => {
    return filterCustomFieldsBySession(this.goal.sessionId, customFields);
  };

  closeGoalsLimitWarning(currentView: CreateFirstGoalFormCurrentView): void {
    if (currentView === "startWithTemplate" || this.isCurrentCallExternal) {
      this.goalsLimitReach = { ...this.goalsLimitReach, selectedSession: false };
    }
    this.showGoalsLimitWarning = false;
    this.isCurrentCallExternal = false;
  }

  shouldShowGoalsLimitWarning(): boolean {
    const currentLimit = this.getCurrentLimit();
    return this.goalsLimitReach[currentLimit] && this.showGoalsLimitWarning;
  }

  isScreenAllowed(): boolean {
    return this.screenAllowed[this.checkIfSessionExternalOrSelected()];
  }

  getCurrentLimit(): "selectedSession" | "externalSession" {
    return this.currentView === "startWithTemplate" || this.isCurrentCallExternal ? "selectedSession" : "externalSession";
  }

  private init(): void {
    if (cachedNewGoal && !isCacheTimedout()) {
      this.goal = cachedNewGoal;
    }

    this.goal.sessionId = this.resolve.externalSessionId;
    const currentSession = this.sessionIdMap[this.resolve.externalSessionId] || {};
    this.setSelectedSessions({ externalSession: currentSession, selectedSession: {} });
    this.setMethodologySettings();
    this.setGoalAssignee({ onInit: true });
  }

  private setMethodologySettings = (): void => {
    const sessionSettings = this.selectedSessions.selectedSession?.settings || this.selectedSessions.externalSession?.settings;
    this.assignGoalPermissions = this.okrMethodologyFacade.getAssignGoalPermissionsSetting(sessionSettings, this.goal);
    this.goalsLimit = this.okrMethodologyFacade.getLimitSetting("goal", sessionSettings);
  };

  private shouldAbortOnMissingExternalSessionId(): boolean {
    if (!this.resolve.externalSessionId) {
      delete this.indicators.loading;
      // do not translate this one
      const error: IUIError = {
        body: "You cannot create an Objective without session!",
      };
      this.uiErrorHandlingService.handleModal(error);
      this.dismiss();
      return true;
    }

    return false;
  }

  private getGoalPolicy(): void {
    this.indicators.loadingPolicy = { progress: true };

    this.policyService.getPolicies().then(
      (allPolicies) => {
        delete this.indicators.loadingPolicy;
        this.goalNamePolicy = getGoalNamePolicy(allPolicies);
      },
      (error) => (this.indicators.loadingPolicy = { error })
    );
  }

  private setGoalAssignee(params = { onInit: false }): void {
    this.assignOwner(
      getDefaultAssigneeId({
        teams: this.teams,
        currentUserId: this.currentUserId,
        assignObjectivePermissions: this.assignGoalPermissions,
      }),
      { onInit: params.onInit }
    );
  }

  private updateCurrentView(targetView: CreateFirstGoalFormCurrentView): void {
    this.updateModalWidth(targetView);
    this.currentView = targetView;
  }

  private updateModalWidth(targetView: CreateFirstGoalFormCurrentView): void {
    const modalViewSizeMap = {
      startWithTemplate: "wide",
      startOnYourOwn: "small",
    };

    const createFirstGoalForm = document.querySelector(".create-first-goal");
    createFirstGoalForm.classList.remove("wide", "small");
    createFirstGoalForm.classList.add(modalViewSizeMap[targetView]);
  }

  private getRecommendedGoals(): void {
    this.indicators.loadingTemplates = { progress: true };

    this.goalService.getGoalTemplatesFromMarketplace({ skip: 0, take: recommendedOkrTemplatesCount }).then(
      (goalTemplatesResponse) => {
        this.recommendedOkrTemplates = goalTemplatesResponse.items;
        delete this.indicators.loadingTemplates;
      },
      (error) => (this.indicators.loadingTemplates = { error })
    );
  }

  private installOkrs(item: IBulkCreateGoalRequest, sessionId: string): void {
    const owners = { ownerIds: [this.selectedOwner.id || this.currentUserId] };
    const okrsToInstall: IBulkCreateGoalRequest[] = [{ ...item, sessionId, ...owners }];

    this.indicators.loading = { progress: true };
    // Install OKR
    this.goalService.createBulkGoals(okrsToInstall).then(
      (installedGoals) => {
        // Get installed OKR
        // response model is the same as goalService.createGoal
        this.goalService.getGoal(installedGoals[0].id).then((goal) => {
          this.analyticsService.track("OKR Template For First OKR Installed");
          this.okrEvents.broadcastGoalsChanged("goalCreated", { goal });
          this.$rootScope.$broadcast("goalsChanged");
          delete this.indicators.loading;
          this.$state.go("gtmhub.home.dashboard._goals._goalSidebar.goal", { id: goal.id });
        });
      },
      (error) => (this.indicators.loading = { error })
    );
  }

  private assignOwner(id: string, { onInit } = { onInit: false }): void {
    this.selectedOwner = this.assigneeIdMap[id];
    this.checkGoalLimitAndPermissions(id, { shouldCheckPermissions: true, onInit })
      .then((limitReached) => {
        if (!limitReached) {
          this.selectMultipleOwners([id]);
        }

        if (!limitReached && this.installOkrsCb) {
          this.installOkrsCb();
          this.installOkrsCb = null;
        }
      })
      .catch(noop);
  }

  private selectMultipleOwners = (ids: string[]): void => {
    if (!ids.length) return;

    this.goal.ownerIds = ids;
    this.goal.assignees = ids.map((id) => this.assigneeIdMap[id]);
  };

  private setSelectedSessions(params: { externalSession: Session; selectedSession: Session }) {
    this.selectedSessions.externalSession = params.externalSession;
    this.selectedSessions.selectedSession = params.selectedSession;
  }

  private checkIfSessionExternalOrSelected() {
    return this.resolve.isOnlyOpenSession !== true && (this.currentView === "startWithTemplate" || this.isCurrentCallExternal) ? "selectedSession" : "externalSession";
  }

  private checkGoalLimitAndPermissions(id: string, { shouldCheckPermissions, onInit } = { shouldCheckPermissions: false, onInit: false }): IPromise<boolean> {
    const targetType = "session";
    const targetId = this.selectedSessions[this.checkIfSessionExternalOrSelected()].id;

    if (!targetId || !id) {
      return this.$q.reject("missing planning session and/or selected owner");
    }

    return this.$q
      .all({
        stats: this.planningSessionService.stats(targetId, { assigneeId: id }),
        ...(shouldCheckPermissions && { currentUserAllowedActions: this.permissionsService.getCurrentUserAllowedActionsPerItem(targetId, targetType) }),
      })
      .then(
        ({ currentUserAllowedActions, stats }) => {
          delete this.indicators.loading;

          this.setAllowedActions({ shouldCheckPermissions }, currentUserAllowedActions);

          this.selectedOwnerGoalsCount = stats.goalCount;
          const currentLimit = this.getCurrentLimit();

          this.goalsLimitReach[currentLimit] = this.selectedOwnerGoalsCount >= this.goalsLimit;
          if (!onInit || this.currentView === "startOnYourOwn") {
            this.showGoalsLimitWarning = true;
          }

          if (onInit && this.resolve.shouldStartWithTemplate) {
            this.switchToView("startWithTemplate", { shouldSkipWarning: true });
          }

          if (this.goalsLimitReach[currentLimit]) {
            delete this.indicators.saving;
          }

          return this.goalsLimitReach[currentLimit];
        },
        (error) => {
          this.uiErrorHandlingService.handleModal(error);
          return this.$q.reject(error);
        }
      );
  }

  private setAllowedActions({ shouldCheckPermissions }: { shouldCheckPermissions: boolean }, currentUserAllowedActions: IUserItemAllowedActions) {
    if (shouldCheckPermissions) {
      this.allowedActions = currentUserAllowedActions.currentUserActions;

      if (this.allowedActions.indexOf("create") !== -1) {
        this.screenAllowed = { ...this.screenAllowed, [this.checkIfSessionExternalOrSelected()]: true };
      } else {
        this.screenAllowed = { ...this.screenAllowed, [this.checkIfSessionExternalOrSelected()]: false };
      }
    }
  }

  private enrichRelevantOkrTemplates(goalTemplates: IOkrTemplate[]) {
    return goalTemplates.map(
      (item) =>
        ({
          id: item.appId,
          name: item.name,
          showIcon: true,
          icon: "goal",
          fields: item,
        } as SingleCollectionSearchItem)
    );
  }
}

export const CreateFirstGoalFormComponent: IComponentOptions = {
  template: require("./create-first-goal-form.html"),
  controller: CreateFirstGoalFormCtrl,
  bindings: {
    close: "&",
    dismiss: "&",
    resolve: "<",
  },
};
